import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { City } from 'country-state-city';

import InputGroupComponent from 'components/groups/InputGroup';
import SelectGroup from 'components/groups/SelectGroup';
import { ControlledSearchSelectGroup } from 'components/shared/form/ControlledSearchSelectGroup';
import FieldSetWithTitle from 'shared/FieldSetWithTitle/components/FieldSetWithTitle';
import Grid from 'components/elements/Grid';
import { IField } from 'components/shared/form/IField';
import useField from 'customers/hooks/useField';
import SelectOptionArray from 'types/model/selectOption';
import { CustomerLead } from '../model/CustomerLead';

export interface CountryOption {
  id: number | string;
  name: string;
  alpha2: string;
}

export interface LeadDetailsProps {
  data: CustomerLead;
  fields: IField[];
  countries: CountryOption[];
}

const numericRangeOptions = (from: number, to: number): SelectOptionArray =>
  Array.from({ length: to - from + 1 }, (_, index) => {
    const value = String(from + index);
    return { id: value, name: value, value };
  });

const LeadDetails = ({ data, fields, countries }: LeadDetailsProps) => {
  const countryField = useField(fields, 'lead_country');
  const cityField = useField(fields, 'lead_city');
  const websiteField = useField(fields, 'lead_website');
  const originScoreField = useField(fields, 'lead_origin_score');
  const valuesScoreField = useField(fields, 'lead_values_score');
  const sizeScoreField = useField(fields, 'lead_size_score');
  const priorityTierField = useField(fields, 'lead_priority_tier');

  const [cityOptions, setCityOptions] = useState<SelectOptionArray>([]);
  const [citiesUnavailable, setCitiesUnavailable] = useState(false);

  const selectedCountry = countries.find(
    (country) => String(country.id) === String(data.countryId)
  );

  useEffect(() => {
    if (!selectedCountry?.alpha2) {
      setCityOptions([]);
      setCitiesUnavailable(false);
      return;
    }

    const cities = City.getCitiesOfCountry(selectedCountry.alpha2) || [];
    if (cities.length > 0) {
      const uniqueNames = Array.from(new Set(cities.map((city) => city.name)));
      setCityOptions(uniqueNames.map((name) => ({ id: name, name, value: name })));
      setCitiesUnavailable(false);
    } else {
      setCityOptions([]);
      setCitiesUnavailable(true);
    }
  }, [selectedCountry?.alpha2]);

  const priorityTierLabel = data.priorityTier
    ? I18n.translate(`leads.priority_tier.${data.priorityTier}`)
    : '—';

  return (
    <FieldSetWithTitle title={I18n.translate('leads.form.lead_details')}>
      <Grid grid={12}>
        <ControlledSearchSelectGroup
          name='customer_lead[country_id]'
          label={I18n.translate('leads.form.country')}
          placeholder={I18n.translate('leads.form.select_country')}
          options={countries}
          value={data.countryId}
          onChange={(value) => data.setCountryId(value)}
          disabled={!countryField?.enabled}
          required={true}
        />

        {citiesUnavailable ? (
          <InputGroupComponent
            label={I18n.translate('leads.form.city')}
            name='customer_lead[city]'
            value={data.city || ''}
            placeholder=''
            onChange={(e) => data.setCity(e.target.value)}
            disabled={!cityField?.enabled || !selectedCountry}
          />
        ) : (
          <ControlledSearchSelectGroup
            name='customer_lead[city]'
            label={I18n.translate('leads.form.city')}
            placeholder={I18n.translate('leads.form.select_city')}
            options={cityOptions}
            value={data.city}
            onChange={(value) => data.setCity(value)}
            disabled={!cityField?.enabled || !selectedCountry}
          />
        )}

        <InputGroupComponent
          label={I18n.translate('leads.form.website')}
          name='customer_lead[website]'
          value={data.website || ''}
          placeholder=''
          onChange={(e) => data.setWebsite(e.target.value)}
          disabled={!websiteField?.enabled}
        />

        <SelectGroup
          name='customer_lead[origin_score]'
          label={I18n.translate('leads.form.origin_score')}
          placeholder={I18n.translate('leads.form.select_origin_score')}
          options={numericRangeOptions(0, 7)}
          value={data.originScore !== undefined ? String(data.originScore) : ''}
          onChange={(e) => data.setOriginScore(e.target.value)}
          required={true}
          disabled={!originScoreField?.enabled}
        />

        <SelectGroup
          name='customer_lead[values_score]'
          label={I18n.translate('leads.form.values_score')}
          placeholder={I18n.translate('leads.form.select_values_score')}
          options={numericRangeOptions(0, 5)}
          value={data.valuesScore !== undefined ? String(data.valuesScore) : ''}
          onChange={(e) => data.setValuesScore(e.target.value)}
          required={true}
          disabled={!valuesScoreField?.enabled}
        />

        <SelectGroup
          name='customer_lead[size_score]'
          label={I18n.translate('leads.form.size_score')}
          placeholder={I18n.translate('leads.form.select_size_score')}
          options={numericRangeOptions(1, 3)}
          value={data.sizeScore !== undefined ? String(data.sizeScore) : ''}
          onChange={(e) => data.setSizeScore(e.target.value)}
          required={true}
          disabled={!sizeScoreField?.enabled}
        />

        <InputGroupComponent
          label={I18n.translate('leads.form.priority_tier')}
          value={priorityTierLabel}
          placeholder=''
          readOnly
          disabled={!priorityTierField?.enabled}
        />
      </Grid>
    </FieldSetWithTitle>
  );
};

export default observer(LeadDetails);
